import { AlertStatus } from "@/alerts/types/machine-alert";
import type { ClassificationLabel, ReviewItemAlert } from "../types/review";

/**
 * Provenance tier of a label — how much human backing it actually has.
 *
 * A reviewer scanning the grid otherwise cannot tell a label a person typed
 * from one a model guessed, and an export built on the second is a very
 * different dataset from one built on the first.
 *
 * Ordered strongest → weakest (RFC-ML-002 D2):
 *
 *   exact        a human reviewed this image in the grid and said so
 *   exact-alert  a human's alert verdict propagated onto this frame
 *   strong       a model label the operator's alert verdict agrees with
 *   weak         a model label nobody has corroborated
 */
export type LabelTier = "exact" | "exact-alert" | "strong" | "weak";

/** Review source the backend stamps when an alert verdict propagates. */
const ALERT_PROPAGATION_SOURCE = "alert_propagation";

/** Review source the backend stamps for a grid review. */
const CLASSIFICATION_REVIEW_SOURCE = "classification_review";

/**
 * Derive a label's tier. Client-side on purpose: every input is already in the
 * listing payload, so this costs nothing and stays correct as the reviewer
 * edits without waiting for a refetch.
 *
 * `alert` is the parent event's verdict (see ReviewItemAlert); pass it so a
 * model label can be promoted to `strong`. Omitted or null → the label can
 * only reach `weak`, which is also what happens before the backend starts
 * sending the field (MIN-258).
 */
export const deriveLabelTier = (
  label: ClassificationLabel,
  alert?: ReviewItemAlert | null,
): LabelTier => {
  // A human's own words on this exact image — nothing outranks it.
  if (label.human_label && label.review_source === CLASSIFICATION_REVIEW_SOURCE) {
    return "exact";
  }

  // A human's verdict, but given to the parent event rather than this frame.
  if (label.review_source === ALERT_PROPAGATION_SOURCE) {
    return "exact-alert";
  }

  // A model label the operator implicitly confirmed by resolving the alert on
  // its event. Ignored deliberately does NOT qualify: the operator rejected
  // that alert, so the model's claim is contradicted, not corroborated.
  if (label.origin === "model" && alert?.status === AlertStatus.Resolved) {
    return "strong";
  }

  return "weak";
};

/**
 * Tailwind classes per tier, from theme tokens so the badges track dark mode.
 *
 * `weak` maps to null rather than a style: it is the default case and by far
 * the most common, so badging it would put a marker on nearly every chip and
 * carry no information. Absence of a badge IS the weak signal.
 */
export const LABEL_TIER_STYLE: Record<LabelTier, string | null> = {
  exact: "border-emerald-500/40 text-emerald-500",
  "exact-alert": "border-sky-500/40 text-sky-500",
  strong: "border-amber-500/40 text-amber-500",
  weak: null,
};

/** Short badge text per tier. */
export const LABEL_TIER_LABEL: Record<LabelTier, string> = {
  exact: "exact",
  "exact-alert": "alert",
  strong: "strong",
  weak: "weak",
};
